import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V14 — "Retro-Futurist"
   1970s "vision of the future." Warm-toned, bold geometric display fonts,
   rounded-corner UI components. Nostalgic but high-performance.
   Righteous (display, 70s rounded geometric) + Red Hat Display (body). */

const warmBg = '#fdf1e3';
const deepBrown = '#3a2518';
const burnt = '#d4622b';
const mustard = '#e8a832';
const retro = '#c94c4c';
const teal = '#2a8a7e';

const features = [
  { num: '01', title: 'Agenda intelligent', desc: 'Créneaux optimisés automatiquement — comme un ordinateur de bord.' },
  { num: '02', title: 'Dossiers patients', desc: 'Historique clinique navigable comme un catalogue rétro-futuriste.' },
  { num: '03', title: 'Facturation fluide', desc: 'Devis et factures pilotés depuis le cockpit de votre cabinet.' },
  { num: '04', title: 'Multi-cabinet', desc: 'Commandez plusieurs stations depuis un panneau de contrôle unique.' },
];

export default function LandingV14() {
  const display = "'Righteous', cursive";
  const body = "'Red Hat Display', sans-serif";

  return (
    <div className="doctif-page" style={{ fontFamily: body, background: warmBg, color: deepBrown }}>
      <Helmet><title>doctif — Retro-Futurist</title></Helmet>

      {/* Warm gradient background */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 80% 60% at 30% 0%, rgba(232,168,50,0.1) 0%, transparent 60%),
          radial-gradient(ellipse 60% 50% at 70% 100%, rgba(212,98,43,0.08) 0%, transparent 50%)
        `,
      }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '24px 0', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            fontFamily: display, fontSize: 36, color: burnt, letterSpacing: '0.02em',
          }}>
            doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 15 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 20, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                  background: i === 13 ? burnt : 'rgba(58,37,24,0.06)',
                  color: i === 13 ? warmBg : '#9a8070',
                  border: `1.5px solid ${i === 13 ? burnt : 'rgba(58,37,24,0.15)'}`,
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              fontFamily: body, fontWeight: 700, fontSize: 13, color: deepBrown,
              padding: '8px 20px', borderRadius: 20,
              border: `2px solid ${deepBrown}`, textDecoration: 'none',
              transition: 'all 0.3s',
            }}>Connexion</a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 48, paddingBottom: 64 }}>
        <div style={{ maxWidth: 720 }}>
          <div className="doctif-fade-up" style={{
            display: 'inline-block', padding: '6px 16px', borderRadius: 20,
            background: `${mustard}22`, border: `1.5px solid ${mustard}44`,
            fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: burnt, marginBottom: 24,
          }}>
            ◉ Mission de demain, design d'hier
          </div>
          <h1 className="doctif-fade-up doctif-delay-1" style={{
            fontFamily: display, fontWeight: 400,
            fontSize: 'clamp(2.6rem, 6.5vw, 5.6rem)', lineHeight: 1.05,
            letterSpacing: '0.01em', marginBottom: 24,
          }}>
            La clinique<br />
            <span style={{ color: burnt }}>du futur,</span><br />
            <span style={{ color: teal }}>version 1974.</span>
          </h1>
          <p className="doctif-fade-up doctif-delay-2" style={{
            fontSize: 17, lineHeight: 1.75, color: '#7a6555', maxWidth: 480, marginBottom: 32,
          }}>
            doctif combine l'optimisme du design rétro-futuriste avec la puissance des systèmes
            modernes. Un cabinet spatial pour praticiens terrestres.
          </p>
          <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 14 }}>
            <a href="/accounts/login/" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 32px', background: burnt, color: warmBg,
              fontFamily: display, fontSize: 16, borderRadius: 28,
              textDecoration: 'none', boxShadow: `0 6px 20px ${burnt}33`,
              transition: 'all 0.3s',
            }}>◉ Décollage</a>
            <a href="#modules" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 32px', border: `2px solid ${deepBrown}`,
              borderRadius: 28, color: deepBrown, fontFamily: display, fontSize: 16,
              textDecoration: 'none', transition: 'all 0.3s',
            }}>Explorer</a>
          </div>
        </div>
      </section>

      {/* ── Stats — Retro dashboard bar ── */}
      <div style={{ background: deepBrown, borderRadius: 24, margin: '0 auto', maxWidth: 1100, position: 'relative', zIndex: 5 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', padding: '24px 32px' }}>
          {[
            { v: '240+', l: 'stations connectées', color: mustard },
            { v: '61k', l: 'rendez-vous / mois', color: burnt },
            { v: '34%', l: 'temps retrouvé', color: teal },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', borderRight: i < 2 ? '1px solid #5a4133' : 'none' }}>
              <div style={{ fontFamily: display, fontSize: 36, color: s.color }}>{s.v}</div>
              <div style={{ fontFamily: body, fontSize: 12, color: '#9a8070', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features — Rounded retro cards ── */}
      <section id="modules" className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 72, paddingBottom: 72 }}>
        <div style={{
          display: 'inline-block', padding: '4px 14px', borderRadius: 16,
          background: `${teal}15`, color: teal, fontWeight: 700,
          fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
        }}>Modules</div>
        <h2 style={{ fontFamily: display, fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', marginBottom: 40, letterSpacing: '0.01em' }}>
          Tableau de bord spatial
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {features.map((f, i) => {
            const colors = [burnt, mustard, teal, retro];
            const c = colors[i % 4];
            return (
              <div key={i} className="doctif-fade-up" style={{
                padding: '32px 28px', borderRadius: 20,
                background: '#fff8ef', border: `2px solid ${c}22`,
                boxShadow: `0 4px 16px rgba(58,37,24,0.06)`,
                animationDelay: `${i * 120}ms`, transition: 'transform 0.3s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <span style={{
                    fontFamily: display, fontSize: 40, color: c, lineHeight: 1, opacity: 0.4,
                  }}>{f.num}</span>
                  <h3 style={{ fontFamily: display, fontSize: 20, color: deepBrown }}>{f.title}</h3>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#7a6555' }}>{f.desc}</p>
                {/* Retro progress indicator */}
                <div style={{ marginTop: 16, height: 6, borderRadius: 3, background: `${c}15`, overflow: 'hidden' }}>
                  <div style={{ width: `${65 + i * 10}%`, height: '100%', borderRadius: 3, background: c }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Retro-Futurist Dashboard preview ── */}
      <section style={{ position: 'relative', zIndex: 5, paddingBottom: 60 }}>
        <div className="doctif-shell">
          <div style={{
            background: deepBrown, borderRadius: 24, padding: '32px', overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <span style={{ fontFamily: display, fontSize: 20, color: warmBg }}>◉ Station de contrôle</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {[retro, mustard, teal].map((c, i) => (
                  <div key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: c }} />
                ))}
              </div>
            </div>
            {/* Gauges row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
              {[
                { label: 'Patients', val: 89, color: teal },
                { label: 'RDV', val: 72, color: burnt },
                { label: 'Facturation', val: 95, color: mustard },
                { label: 'Satisfaction', val: 98, color: retro },
              ].map((g, i) => (
                <div key={i} style={{
                  background: '#4a3628', borderRadius: 16, padding: '16px 14px', textAlign: 'center',
                }}>
                  <div style={{ fontFamily: display, fontSize: 32, color: g.color }}>{g.val}%</div>
                  <div style={{ fontSize: 11, color: '#9a8070', marginTop: 4 }}>{g.label}</div>
                  <div style={{ marginTop: 8, height: 6, borderRadius: 3, background: '#5a4133' }}>
                    <div style={{ width: `${g.val}%`, height: '100%', borderRadius: 3, background: g.color }} />
                  </div>
                </div>
              ))}
            </div>
            {/* Entries */}
            {['Dr. Martin — Contrôle — 09:00', 'Dr. Leroy — Détartrage — 10:30', 'Dr. Fabre — Implant — 14:00'].map((e, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', padding: '12px 0',
                borderTop: '1px solid #4a3628', fontSize: 13, color: '#b5a08a',
              }}>
                <span>{e}</span>
                <span style={{
                  padding: '2px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: i === 1 ? `${mustard}22` : `${teal}22`,
                  color: i === 1 ? mustard : teal,
                }}>{i === 1 ? 'En cours' : 'Confirmé'}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingBottom: 60 }}>
        <div style={{
          display: 'inline-block', padding: '4px 14px', borderRadius: 16,
          background: `${retro}15`, color: retro, fontWeight: 700,
          fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24,
        }}>Transmissions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {[
            { q: 'On dirait le cockpit d\'une station spatiale — mais pour mon cabinet.', a: 'Dr. Elise Moreau', r: 'Paris' },
            { q: 'Le design rétro rend chaque tâche administrative presque fun.', a: 'Dr. Julien Fabre', r: 'Lyon' },
            { q: 'Mes patients adorent l\'esthétique. C\'est comme voyager dans le temps.', a: 'Dr. Nadia Hamdi', r: 'Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '24px', borderRadius: 16,
              background: '#fff8ef', border: `1.5px solid rgba(58,37,24,0.1)`,
              boxShadow: '0 2px 8px rgba(58,37,24,0.04)',
            }}>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: '#7a6555', marginBottom: 16, fontStyle: 'italic' }}>
                « {t.q} »
              </p>
              <p style={{ fontFamily: display, fontSize: 14, color: deepBrown }}>{t.a}</p>
              <p style={{ fontSize: 12, color: '#9a8070' }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust bar ── */}
      <div style={{ background: `${deepBrown}08`, borderTop: `1px solid ${deepBrown}15`, borderBottom: `1px solid ${deepBrown}15`, position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', padding: '18px 0', textAlign: 'center' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Uptime', v: '99.9%' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i}>
              <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9a8070', marginBottom: 2 }}>{t.k}</div>
              <div style={{ fontFamily: display, fontSize: 16, color: deepBrown }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 72, paddingBottom: 72, textAlign: 'center' }}>
        <h2 style={{
          fontFamily: display, fontSize: 'clamp(1.8rem, 4.5vw, 3.4rem)',
          letterSpacing: '0.01em', lineHeight: 1.1, marginBottom: 28,
        }}>
          Prêt pour<br /><span style={{ color: burnt }}>le décollage ?</span>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '16px 44px', background: burnt, color: warmBg,
          fontFamily: display, fontSize: 18, borderRadius: 28,
          textDecoration: 'none', boxShadow: `0 8px 28px ${burnt}33`,
          transition: 'all 0.3s',
        }}>◉ Lancer la mission</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, padding: '16px 0', borderTop: `1px solid ${deepBrown}15` }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: display, fontSize: 14, color: '#9a8070' }}>doctif — Direction Retro-Futurist</span>
          <span style={{ fontSize: 12, color: '#b5a08a' }}>v14.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
